import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Upload, FileText, History, Flag, LogOut, Users, Lock, Eye, EyeOff, RefreshCw, Trash2, Plus, X, Calendar, Settings, Mail, BarChart3, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AuthProvider, useAuth, getAuthHeaders } from "@/context/AuthContext";
import HomePage from "@/pages/HomePage";
import ResultPage from "@/pages/ResultPage";
import PDFViewerPage from "@/pages/PDFViewerPage";
import { ForgotPasswordPage, ResetPasswordWithTokenPage } from "@/pages/AuthPages";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import EmailLogsPage from "@/pages/EmailLogsPage";
import UsageAnalyticsPage from "@/pages/UsageAnalyticsPage";
import CloudDashboardPage from "@/pages/CloudDashboardPage";
import ChangePasswordModal from "@/pages/ChangePasswordModal";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

// Login Page
function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      auth.login(response.data.token, response.data.user);
      toast.success("Welcome back!");
      navigate("/");
    } catch (error) {
      const msg = error.response?.data?.detail || "Login failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="w-[400px] bg-zinc-900/50 border-white/5">
          <CardHeader className="text-center pb-2">
            <h1 className="text-3xl font-bold text-white">q<span className="text-blue-500">Pro</span></h1>
            <p className="text-zinc-500 text-sm">Quotation Comparison Tool</p>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Email</label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="admin@qpro.com" className="bg-zinc-800 border-zinc-700 text-white" required data-testid="login-email" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Password</label>
                  <div className="relative">
                    <Input value={password} onChange={(e) => setPassword(e.target.value)} type={showPass ? "text" : "password"} placeholder="********" className="bg-zinc-800 border-zinc-700 text-white pr-10" required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500" data-testid="login-btn">
                  {loading && <RefreshCw className="animate-spin mr-2" size={16} />}
                  Sign In
                </Button>
              </div>
            </form>
            <div className="mt-4 text-center space-x-3">
              <button onClick={() => navigate("/forgot-password")} className="text-xs text-zinc-500 hover:text-blue-400">Forgot password?</button>
              <span className="text-zinc-700">·</span>
              <button onClick={() => navigate("/privacy")} className="text-xs text-zinc-500 hover:text-blue-400">Privacy Policy</button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Sidebar
function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const navItems = [
    { path: "/", icon: Upload, label: "New Comparison", testId: "nav-compare" },
    { path: "/history", icon: History, label: "History", testId: "nav-history" },
  ];

  const adminItems = auth.user?.is_admin ? [
    { path: "/flagged-words", icon: Flag, label: "Flagged Words", testId: "nav-flagged" },
    { path: "/users", icon: Users, label: "User Management", testId: "nav-users" },
    { path: "/admin/settings", icon: Settings, label: "Admin Settings", testId: "nav-admin-settings" },
    { path: "/admin/email-logs", icon: Mail, label: "Email Logs", testId: "nav-email-logs" },
    { path: "/admin/analytics", icon: BarChart3, label: "Usage Analytics", testId: "nav-analytics" },
    { path: "/admin/cloud-dashboard", icon: Cloud, label: "Cloud Dashboard", testId: "nav-cloud-dashboard" },
  ] : [];

  return (
    <div className="fixed left-0 top-0 h-screen w-64 bg-zinc-950 border-r border-white/10 p-6 flex flex-col">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">q<span className="text-blue-500">Pro</span></h1>
        <p className="text-xs text-zinc-500 mt-1">Quotation Comparison</p>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button key={item.path} onClick={() => navigate(item.path)} data-testid={item.testId} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${isActive ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "text-zinc-400 hover:bg-white/5"}`}>
              <Icon size={18} />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          );
        })}
        
        {/* Admin Section */}
        {adminItems.length > 0 && (
          <>
            <div className="pt-4 mt-4 border-t border-white/10">
              <p className="text-xs text-zinc-600 uppercase tracking-wider px-4 mb-2">Admin</p>
            </div>
            {adminItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button key={item.path} onClick={() => navigate(item.path)} data-testid={item.testId} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${isActive ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "text-zinc-400 hover:bg-white/5"}`}>
                  <Icon size={18} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </>
        )}
      </nav>
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
            <span className="text-blue-400 text-xs font-bold">{auth.user?.username?.charAt(0).toUpperCase() || "?"}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{auth.user?.username || ""}</p>
            <p className="text-xs text-zinc-500 truncate">{auth.user?.email || ""}</p>
          </div>
        </div>
        <button onClick={() => setChangePasswordOpen(true)} data-testid="nav-change-password" className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-zinc-400 hover:bg-white/5 transition-all">
          <Lock size={16} />
          <span className="text-sm">Change Password</span>
        </button>
        <button onClick={auth.logout} className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-all">
          <LogOut size={16} />
          <span className="text-sm">Logout</span>
        </button>
      </div>
      <ChangePasswordModal open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}

// History Page
function HistoryPage() {
  const auth = useAuth();
  const isAdmin = !!auth.user?.is_admin;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterQid, setFilterQid] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterQid.trim()) params.append("quotation_id", filterQid.trim());
    if (filterCompany.trim()) params.append("company", filterCompany.trim());
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (isAdmin && filterUserId) params.append("user_id", filterUserId);
    const url = `${API}/comparisons${params.toString() ? `?${params.toString()}` : ""}`;
    try {
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setList(res.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/users`, { headers: getAuthHeaders() })
      .then((res) => setUsers(res.data || []))
      .catch(() => {});
  }, [isAdmin]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/comparisons/${id}`, { headers: getAuthHeaders() });
      setList(list.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="animate-spin text-blue-500" size={32} /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">History</h1>
          <p className="text-zinc-500 mt-1">{isAdmin ? "View all users' comparisons" : "View your past comparisons"}</p>
        </div>

        <Card className="bg-zinc-900/50 border-white/5 mb-6">
          <CardContent className="p-4">
            <div className={`grid ${isAdmin ? "grid-cols-7" : "grid-cols-6"} gap-4 items-end`}>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Quotation ID</label>
                <Input value={filterQid} onChange={(e) => setFilterQid(e.target.value)} placeholder="Search..." className="bg-zinc-800 border-zinc-700 text-white" data-testid="history-filter-qid" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Company</label>
                <Input value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} placeholder="Search..." className="bg-zinc-800 border-zinc-700 text-white" data-testid="history-filter-company" />
              </div>
              {isAdmin && (
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1"><Users size={12} /> User</label>
                  <select
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-white text-sm"
                    data-testid="history-filter-user"
                  >
                    <option value="">All users</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1"><Calendar size={12} /> From</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="history-filter-from" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block flex items-center gap-1"><Calendar size={12} /> To</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="history-filter-to" />
              </div>
              <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-500" data-testid="history-search-btn">Search</Button>
              <Button variant="secondary" onClick={() => { setFilterQid(""); setFilterCompany(""); setDateFrom(""); setDateTo(""); setFilterUserId(""); }} className="bg-zinc-800 hover:bg-zinc-700" data-testid="history-clear-btn">Clear</Button>
            </div>
          </CardContent>
        </Card>

        {list.length > 0 ? (
          <div className="space-y-3">
            {list.map((c) => (
              <Card key={c.id} className="bg-zinc-900/50 border-white/5 hover:border-white/10 cursor-pointer" onClick={() => navigate(`/result/${c.id}`)} data-testid={`history-row-${c.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <FileText className="text-zinc-400" size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        <span className="text-blue-400">{c.quotation_id}</span>
                        <span className="text-zinc-500 ml-1">v{c.version}</span>
                      </p>
                      <p className="text-xs text-zinc-500">{c.company_name} - {new Date(c.comparison_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isAdmin && (
                      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800 border border-white/5" data-testid={`history-user-${c.id}`}>
                        <Users size={12} className="text-zinc-500" />
                        <span className="text-xs text-zinc-300">{c.created_by_username || "Unknown"}</span>
                      </div>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${c.total_discrepancies > 0 ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                      {c.total_discrepancies} findings
                    </span>
                    <Button variant="ghost" size="icon" onClick={(e) => handleDelete(c.id, e)} className="text-zinc-500 hover:text-red-400">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-zinc-900/30 border-white/5">
            <CardContent className="p-12 text-center">
              <History className="mx-auto text-zinc-600 mb-4" size={48} />
              <p className="text-zinc-400">No comparisons yet</p>
            </CardContent>
          </Card>
        )}
      </div>
    </motion.div>
  );
}

// Settings (Flagged Words)
function SettingsPage() {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState("");
  const [newDesc, setNewDesc] = useState("");

  useEffect(() => {
    axios.get(`${API}/flagged-words`, { headers: getAuthHeaders() })
      .then((res) => setWords(res.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    try {
      const res = await axios.post(`${API}/flagged-words`, { word: newWord.trim(), description: newDesc.trim() || null, severity: "high" }, { headers: getAuthHeaders() });
      setWords([res.data, ...words]);
      setNewWord("");
      setNewDesc("");
      toast.success("Added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/flagged-words/${id}`, { headers: getAuthHeaders() });
      setWords(words.filter((w) => w.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="animate-spin text-blue-500" size={32} /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">Flagged Words</h1>
          <p className="text-zinc-500 mt-1">Words to flag in Partner PO Terms & Conditions</p>
        </div>

        <Card className="bg-zinc-900/50 border-white/5 mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-xs text-zinc-500 mb-1 block">Word/Phrase</label>
                <Input value={newWord} onChange={(e) => setNewWord(e.target.value)} placeholder="e.g., anticorruption" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-zinc-500 mb-1 block">Description</label>
                <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Optional" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-500"><Plus size={16} className="mr-1" />Add</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-0 divide-y divide-white/5">
            {words.length > 0 ? words.map((w) => (
              <div key={w.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Flag className="text-red-400" size={16} />
                  <div>
                    <p className="text-sm font-semibold text-white">{w.word}</p>
                    {w.description && <p className="text-xs text-zinc-500">{w.description}</p>}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(w.id)} className="text-zinc-500 hover:text-red-400">
                  <X size={16} />
                </Button>
              </div>
            )) : (
              <div className="p-8 text-center text-zinc-500">No flagged words</div>
            )}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

// User Management (Admin only)
function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    axios.get(`${API}/users`, { headers: getAuthHeaders() })
      .then((res) => setUsers(res.data))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!email.trim() || !username.trim() || !password) {
      toast.error("Fill all fields");
      return;
    }
    try {
      const res = await axios.post(`${API}/users`, { email: email.trim(), username: username.trim(), password, is_admin: isAdmin }, { headers: getAuthHeaders() });
      setUsers([...users, res.data]);
      setEmail("");
      setUsername("");
      setPassword("");
      setIsAdmin(false);
      toast.success("User created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`, { headers: getAuthHeaders() });
      setUsers(users.filter((u) => u.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="animate-spin text-blue-500" size={32} /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          <p className="text-zinc-500 mt-1">Add and manage users</p>
        </div>

        <Card className="bg-zinc-900/50 border-white/5 mb-6">
          <CardHeader><CardTitle className="text-lg text-white">Add New User</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Email</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@company.com" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Username</label>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="johndoe" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Password</label>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="********" className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-sm text-zinc-400">
                  <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} className="rounded" />
                  Admin
                </label>
                <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-500"><Plus size={16} className="mr-1" />Add User</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-white/5">
          <CardHeader><CardTitle className="text-lg text-white">Users ({users.length})</CardTitle></CardHeader>
          <CardContent className="p-0 divide-y divide-white/5">
            {users.map((u) => (
              <div key={u.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-blue-400 text-xs font-bold">{u.username.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <div className="flex items-center text-sm font-semibold text-white">
                      {u.username}
                      {u.is_admin && <Badge className="ml-2 bg-blue-500/20 text-blue-400">Admin</Badge>}
                    </div>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} className="text-zinc-500 hover:text-red-400">
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
}

// Main App
function AppContent() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        const isAuthCall = err.config?.url?.includes('/api/auth/');
        if (err.response?.status === 401 && !isAuthCall) {
          auth.logout();
          toast.error("Session expired. Please log in again.");
          navigate('/login');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [auth, navigate]);

  if (!auth.isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordWithTokenPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/result/:id" element={<ResultPage />} />
          <Route path="/viewer/:id" element={<PDFViewerPage />} />
          <Route path="/flagged-words" element={auth.user?.is_admin ? <SettingsPage /> : <Navigate to="/" />} />
          <Route path="/users" element={auth.user?.is_admin ? <UsersPage /> : <Navigate to="/" />} />
          <Route path="/admin/settings" element={auth.user?.is_admin ? <AdminSettingsPage /> : <Navigate to="/" />} />
          <Route path="/admin/email-logs" element={auth.user?.is_admin ? <EmailLogsPage /> : <Navigate to="/" />} />
          <Route path="/admin/analytics" element={auth.user?.is_admin ? <UsageAnalyticsPage /> : <Navigate to="/" />} />
          <Route path="/admin/cloud-dashboard" element={auth.user?.is_admin ? <CloudDashboardPage /> : <Navigate to="/" />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App bg-[#09090B] min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
          <Toaster position="bottom-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
